import {
  Autocomplete,
  type AutocompleteProps,
  Box,
  CircularProgress,
  createFilterOptions,
  TextField,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import type { SxProps, Theme } from "@mui/material/styles";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { SEARCH_DEBOUNCE_MS } from "@helpers/constants";

import {
  RESOURCE_TYPE_ICONS,
  RESOURCE_TYPE_LABELS,
  RESOURCE_TYPE_PLURAL_LABELS,
} from "../../data/resourceTypeIcons";
import { useDebounce } from "../../hooks/useDebounce";
import { useResourceBookmarks } from "../../hooks/useResourceBookmarks";
import {
  type ResourceMetadata,
  useGetResourceMetadataQuery,
} from "../../store/api/resourceApi";
import {
  RESOURCE_NAME_FIELD,
  RESOURCE_TYPES_ARRAY,
  type ResourceType,
  toResourceType,
} from "../../store/api/resourceTypes";
import DescriptiveTooltip from "../DescriptiveTooltip";
import { inferSearchFilterType } from "../NameIdSearch/NameIdSearch";
import ResourceStarButton, {
  RESOURCE_STAR_HOVER_STYLES,
} from "../ResourceStarButton/ResourceStarButton";
import {
  MIN_SEARCH_LENGTH,
  type TypeSearchResult,
  useAccountSearch,
  useDataSearch,
  useJobSearch,
  useRoleSearch,
  useServiceSearch,
  useUserSearch,
} from "./useResourceTypeSearch";

export type ResourceItem = {
  id: string;
  fullName?: string;
  name?: string;
  account?: string;
  organization?: string;
  resourceType?: ResourceType;
  urn?: string;
};

const OptionContainer = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: 8,
  width: "100%",
  ...RESOURCE_STAR_HOVER_STYLES,
});

const OptionDetails = styled(Box)({
  flexGrow: 1,
  minWidth: 0,
});

function mapMetadataToResourceItem(metadata: ResourceMetadata): ResourceItem {
  return {
    id: metadata.id,
    fullName: metadata.fullName ?? metadata.name ?? metadata.id,
    name: metadata.name ?? metadata.fullName ?? metadata.id,
    urn: metadata.urn,
  };
}

/**
 * Generic search-driven resource selector: RTK Query search with a 2-char
 * minimum, 300ms debounce, and an ID-search fallback via the metadata endpoint.
 * Intended to eventually replace the purpose-built Account/UserRole selectors.
 *
 * Follow-up story will add: multi-select and a full `loadStrategy` (all / count).
 */
export interface ResourceSelectorProps<T extends ResourceItem> {
  /**
   * Resource types to search. Pass a single type (e.g., "data") to search only
   * that endpoint; pass an array (e.g., ["data", "job"]) to search multiple;
   * pass an empty array [] to search all types.
   */
  resourceType: ResourceType | ResourceType[];
  /** The currently selected resource, or null when nothing is selected. */
  value: T | null;
  /** Called when the user selects or clears a resource. Receives null on clear. */
  onChange: (value: T | null) => void;
  /** "none" (default) = search-only. "all" / "count" reserved for future use. TODO */
  // loadStrategy?: "all" | "count" | "none";
  /** Label shown above the input. Defaults to "Search {resourceType}". */
  label?: string;
  /** Placeholder text shown inside the input. Defaults to "Search for {resourceType}...". */
  placeholder?: string;
  /**
   * Accessible name applied as `aria-label` on the underlying <input>. Use when
   * the field is shown without a visible `label` so screen readers announce the
   * combobox with context rather than as an unnamed field.
   */
  ariaLabel?: string;
  /** When true, the input is rendered in a disabled state. */
  disabled?: boolean;
  /**
   * External loading signal, ORed into the spinner shown in the input.
   * Search mode derives its own loading state from its fetch hooks and
   * doesn't need this; client-filter mode (`defaultOptions`) has no
   * visibility into whether the caller's options list is still loading, so
   * callers in that mode should pass it here.
   */
  loading?: boolean;
  /** When true, the input is marked required (shows asterisk). */
  required?: boolean;
  /**
   * Whether to fall back to the metadata endpoint when the normal search
   * returns no results. When a single resource type is configured it's
   * included to disambiguate the lookup.
   */
  metadataFallback?: boolean;
  /** When true, the input receives focus on mount. Useful when the selector is remounted after a selection. */
  autoFocus?: boolean;
  /** MUI sx prop forwarded to the root Autocomplete element. */
  sx?: SxProps<Theme>;
  /**
   * Optional element rendered as a leading adornment inside the input (e.g. a
   * search icon). Sits before the selected-resource type icon. Style it at the
   * call site to control color/spacing.
   */
  startIcon?: React.ReactNode;
  /**
   * Enables starring and recently-used tracking. Instances sharing the same
   * `cacheId` (and resource type) share one starred/recents list; different
   * `cacheId`s get isolated lists. Omit to disable this entirely.
   */
  cacheId?: string;
  /**
   * If provided, the option with this ID is pinned to a special group
   * (e.g., the current user's sandbox account). Resolved by looking the ID
   * up in `defaultOptions`/bookmarks — pass `sandboxItem` instead (or in
   * addition) when there's no full list to look it up in, e.g. in search
   * mode before it's ever been searched/selected.
   */
  sandboxId?: string;
  /** Label for the sandbox group. Defaults to "Your sandbox". */
  sandboxLabel?: string;
  /**
   * Fully resolved sandbox item, used instead of looking `sandboxId` up in
   * `defaultOptions`/bookmarks. Needed in search mode, where there's no full
   * list to resolve an id-only `sandboxId` against.
   */
  sandboxItem?: T;
  /**
   * Static list of all options. When provided, the selector filters this list
   * locally instead of searching the server.
   */
  defaultOptions?: T[];
  /**
   * If true, arbitrary input is allowed. On blur, if the input matches an
   * option it is selected; otherwise `onFreeSoloBlur` is called with the raw value.
   */
  freeSolo?: boolean;
  /** Called with the raw input value on blur when `freeSolo` is true and no option matched. */
  onFreeSoloBlur?: (value: string | null) => void;
  /** Label for the bookmark group. Defaults to "Suggested". */
  bookmarkGroupLabel?: string;
  /** Label for the catch-all group. Defaults to "All". */
  allGroupLabel?: string;
  /** Optional error text rendered under the input. */
  error?: string | null;
  /** Props forwarded to the inner TextField's InputLabel. */
  inputLabelProps?: React.ComponentProps<typeof TextField>["InputLabelProps"];
  /**
   * Items to seed as recents if the bookmark cache is empty. Useful for
   * one-time migration from legacy comma-joined storage keys.
   */
  initialRecents?: T[];
  /**
   * Called once `initialRecents` has actually been seeded into the bookmark
   * cache (i.e. the cache was empty and the seed wrote). Useful for cleaning
   * up a legacy storage key now that it's been migrated.
   */
  onInitialRecentsSeeded?: () => void;
  /**
   * Overrides the default "no options" text (e.g. "No user found"). Useful
   * when `freeSolo` input can still resolve to something outside this list
   * on blur (e.g. `UserRoleSelector` also accepts role names), so the empty
   * state shouldn't read as a dead end.
   */
  noOptionsText?: string;
  /**
   * Overrides the client-filter mode's option filter. In client-filter mode
   * (i.e. `defaultOptions` provided), MUI's default filter narrows the list
   * against `getOptionLabel` — pass this to widen the match to extra fields
   * like a user's email. Ignored in search mode.
   */
  filterOptions?: AutocompleteProps<
    ResourceItem,
    false,
    false,
    true
  >["filterOptions"];
  /**
   * Optional adornment rendered before each option's label (e.g. a badge).
   */
  renderOptionIcon?: (option: T) => React.ReactNode;
}

function ResourceSelector<T extends ResourceItem>({
  resourceType,
  value,
  onChange,
  // loadStrategy: _loadStrategy = "none",
  label,
  placeholder,
  ariaLabel,
  disabled,
  loading: externalLoading = false,
  required,
  metadataFallback = false,
  autoFocus,
  sx,
  startIcon,
  cacheId,
  sandboxId,
  sandboxLabel = "Your sandbox",
  sandboxItem: sandboxItemProp,
  defaultOptions,
  freeSolo = false,
  onFreeSoloBlur,
  bookmarkGroupLabel = "Suggested",
  allGroupLabel = "All",
  error = null,
  inputLabelProps,
  initialRecents,
  onInitialRecentsSeeded,
  noOptionsText: noOptionsTextOverride,
  filterOptions: filterOptionsProp,
  renderOptionIcon,
}: ResourceSelectorProps<T>) {
  const [inputValue, setInputValue] = useState("");
  const [debouncedQuery] = useDebounce(inputValue, SEARCH_DEBOUNCE_MS, {
    leading: true,
  });

  // When the selection is cleared externally (e.g. another filter takes over),
  // empty the input too — otherwise the just-deselected resource lingers in the
  // box. We suppress MUI's own empty "reset" (see onInputChange) to protect
  // in-progress typing, so the typing case flags itself here to opt out.
  const clearedByTyping = useRef(false);
  // True from the moment we've told the caller the selection is cleared until
  // the `value` prop actually confirms it. While pending, `value` is stale
  // (still the pre-clear item) from callers whose state round-trips async
  // (e.g. through the URL/router) rather than updating in the same render.
  // MUI's own blur handler resyncs the input text from that stale `value`
  // (clearOnBlur, on by default without freeSolo), which would otherwise
  // re-fill the box with the just-cleared label — see repro in
  // ResourceSelector.test.tsx's "clear button" describe block.
  const clearPending = useRef(false);
  const prevValue = useRef(value);
  useEffect(() => {
    if (prevValue.current && !value) {
      if (!clearedByTyping.current) {
        setInputValue("");
      }
      clearPending.current = false;
    } else if (!prevValue.current && value) {
      clearedByTyping.current = false;
    }
    prevValue.current = value;
  }, [value]);

  // Normalize resourceType to always be an array (empty array = search all types)
  const activeTypes: readonly ResourceType[] = useMemo(() => {
    const types = Array.isArray(resourceType) ? resourceType : [resourceType];
    return types.length === 0 ? RESOURCE_TYPES_ARRAY : types;
  }, [resourceType]);
  const isTypeActive = useCallback(
    (type: ResourceType) => activeTypes.includes(type),
    [activeTypes],
  );

  const bookmarks = useResourceBookmarks<ResourceItem>({
    resourceType: activeTypes,
    cacheId: cacheId ?? "__disabled__",
  });

  // Optional one-time seed from legacy comma-joined storage or parent-computed
  // recents. Guarded by cacheId so search-only instances don't write state.
  useEffect(() => {
    if (!cacheId || !initialRecents || initialRecents.length === 0) return;
    const didSeed = bookmarks.seedRecentsIfEmpty(
      initialRecents as ResourceItem[],
    );
    if (didSeed) onInitialRecentsSeeded?.();
  }, [cacheId, initialRecents, bookmarks, onInitialRecentsSeeded]);

  // Pick the name field from the option's own type ("all" mode tags each one);
  // fall back to the single configured type otherwise.
  const singleType: ResourceType | undefined =
    activeTypes.length === 1 ? activeTypes[0] : undefined;
  const getOptionLabel = useCallback(
    (option: ResourceItem) => {
      const type = option.resourceType ?? singleType;
      const field = type ? RESOURCE_NAME_FIELD[type] : "fullName";
      return option[field] ?? option.fullName ?? option.name ?? option.id ?? "";
    },
    [singleType],
  );

  // Keep the input text in sync when the controlled value changes to a new
  // selection (e.g. an account ID resolved from a fullName). We key off the
  // selected id so a refetch that returns the same object doesn't overwrite
  // in-progress typing. We also track the label we last wrote in: some
  // callers resolve a selection in two steps (an id-only stub, e.g. right
  // after a free-solo blur resolves to a role by name, followed moments
  // later by the fetched object with the same id) — if the input still shows
  // exactly what we set it to on the first step, it's safe to upgrade it to
  // the fuller label on the second, even though the id didn't change.
  const previousSelectedId = useRef<string | null>(null);
  const previousSyncedLabel = useRef<string | null>(null);
  useEffect(() => {
    const currentId = value?.id ?? null;
    if (currentId !== null) {
      const idChanged = currentId !== previousSelectedId.current;
      const stillShowingLastSync =
        previousSyncedLabel.current !== null &&
        inputValue === previousSyncedLabel.current;
      if (idChanged || stillShowingLastSync) {
        const label = getOptionLabel(value as ResourceItem);
        if (label) {
          if (inputValue !== label) {
            setInputValue(label);
          }
          previousSyncedLabel.current = label;
        }
      }
    }
    previousSelectedId.current = currentId;
  }, [value, inputValue, getOptionLabel]);

  // After a pick, MUI writes the label back into the input; don't treat that as
  // a fresh query or we'd re-search the just-selected item. Typing clears
  // `value` (see onInputChange), re-enabling search.
  const matchesSelectedValue =
    value != null && inputValue === getOptionLabel(value);
  const useClientFilter = Boolean(defaultOptions);
  const shouldSearch =
    !useClientFilter &&
    !matchesSelectedValue &&
    debouncedQuery.length >= MIN_SEARCH_LENGTH;
  const isIdSearch =
    shouldSearch && inferSearchFilterType(debouncedQuery) === "id";
  const isNameSearch = shouldSearch && !isIdSearch;

  // One hook call per resource type — each normalizes its own name-search vs.
  // ID-search query into a common { items, isFetching } shape. Adding a new
  // ResourceType means writing one more hook in useResourceTypeSearch.ts and
  // adding one line each below and in `searchByType`; the aggregation below
  // (isFetchingActive, freshItems) is generic and needs no further changes.
  const searchArgs = { debouncedQuery, isNameSearch, isIdSearch };
  const dataSearch = useDataSearch({
    ...searchArgs,
    isActive: isTypeActive("data"),
  });
  const jobSearch = useJobSearch({
    ...searchArgs,
    isActive: isTypeActive("job"),
  });
  const serviceSearch = useServiceSearch({
    ...searchArgs,
    isActive: isTypeActive("service"),
  });
  const accountSearch = useAccountSearch({
    ...searchArgs,
    isActive: isTypeActive("account"),
  });
  const userSearch = useUserSearch({
    ...searchArgs,
    isActive: isTypeActive("user"),
  });
  const roleSearch = useRoleSearch({
    ...searchArgs,
    isActive: isTypeActive("role"),
  });

  const searchByType: Record<ResourceType, TypeSearchResult> = useMemo(
    () => ({
      data: dataSearch,
      job: jobSearch,
      service: serviceSearch,
      account: accountSearch,
      user: userSearch,
      role: roleSearch,
    }),
    [
      dataSearch,
      jobSearch,
      serviceSearch,
      accountSearch,
      userSearch,
      roleSearch,
    ],
  );

  // True while any active type has a request in flight (new search or refetch).
  const isFetchingActive = RESOURCE_TYPES_ARRAY.some(
    (t) => isTypeActive(t) && searchByType[t].isFetching,
  );

  // Freshest results across active types. Mid-flight this can be partial or
  // stale (RTK keeps `data` until the new response lands), so we don't render it
  // directly — it's committed to `currentItems` only once settled.
  const freshItems = useMemo((): ResourceItem[] => {
    // Tag each result with its type so multi-type search can show a type icon
    // and pick the right name field per option.
    return RESOURCE_TYPES_ARRAY.filter(isTypeActive).flatMap((t) =>
      searchByType[t].items.map((item) => ({ ...item, resourceType: t })),
    );
  }, [isTypeActive, searchByType]);

  // Keep the previous settled results on screen while a new search is in flight,
  // then swap to the fresh results atomically once every active type has resolved
  // (including swapping to an empty list, which surfaces the "no results" text).
  const [currentItems, setCurrentItems] = useState<ResourceItem[]>([]);
  const freshItemsKey = useMemo(
    () => freshItems.map((item) => item.id).join(","),
    [freshItems],
  );
  const freshItemsRef = useRef(freshItems);
  freshItemsRef.current = freshItems;
  const previousFreshItemsKeyRef = useRef(freshItemsKey);
  useEffect(() => {
    if (
      !isFetchingActive &&
      freshItemsKey !== previousFreshItemsKeyRef.current
    ) {
      previousFreshItemsKeyRef.current = freshItemsKey;
      setCurrentItems(freshItemsRef.current);
    }
  }, [isFetchingActive, freshItemsKey]);

  // Metadata fallback: when normal search settles with no results, try the metadata endpoint
  const normalSearchNoResults =
    shouldSearch && !isFetchingActive && currentItems.length === 0;
  const { data: metadataResult, isFetching: isFetchingMetadata } =
    useGetResourceMetadataQuery(
      {
        identifier: debouncedQuery,
        ...(singleType ? { type: singleType } : {}),
      },
      {
        skip:
          !metadataFallback ||
          !normalSearchNoResults ||
          debouncedQuery.length < MIN_SEARCH_LENGTH,
      },
    );

  const isLoading =
    externalLoading ||
    isFetchingActive ||
    (normalSearchNoResults && isFetchingMetadata);

  const metadataOption = useMemo(() => {
    if (!normalSearchNoResults || !metadataResult) return null;
    const metaType = toResourceType(metadataResult.type);
    if (!metaType || !isTypeActive(metaType)) return null;
    return {
      ...mapMetadataToResourceItem(metadataResult),
      resourceType: metaType,
    };
  }, [normalSearchNoResults, metadataResult, isTypeActive]);

  // With nothing typed, show the starred/recents default view (if bookmarking
  // is enabled) instead of the empty "type to search" state.
  const showDefaultItems = Boolean(cacheId) && inputValue.length === 0;
  const hasCache = Boolean(cacheId);

  const defaultItemIds = useMemo(
    () => new Set(bookmarks.defaultItems.map((item) => item.id)),
    [bookmarks.defaultItems],
  );
  const clientOptionsById = useMemo(
    () => new Map(defaultOptions?.map((item) => [item.id, item]) ?? []),
    [defaultOptions],
  );

  // MUI blanks state.inputValue when it matches the selected value's label on
  // popup re-open (its "browse the list after selecting" affordance). With
  // freeSolo that also triggers for typed-but-not-committed text, unfiltering
  // the whole list on re-focus. Force the real input value through the filter.
  const filterOptionsForced = useMemo(() => {
    const base = filterOptionsProp ?? createFilterOptions<ResourceItem>();
    return (options: ResourceItem[], state: Parameters<typeof base>[1]) =>
      base(options, { ...state, inputValue });
  }, [filterOptionsProp, inputValue]);

  // Grouping applies whenever a sandbox pin or bookmark cache is enabled,
  // regardless of client-filter vs. search mode — both modes can surface a
  // sandboxed/starred/recent item alongside plain results.
  const shouldGroup = Boolean(sandboxId) || hasCache;

  // Single source of truth for both the group label (MUI's groupBy) and the
  // sort priority used to make same-group items contiguous — MUI renders a
  // new header every time groupBy's return value changes, even for a group
  // seen earlier, so un-sorted multi-type results would show "All / Suggested
  // / All" style repeats instead of one clean run per group.
  const resolveGroup = useCallback(
    (option: ResourceItem): { priority: number; label: string } => {
      if (sandboxId && option.id === sandboxId) {
        return { priority: 0, label: sandboxLabel };
      }
      if (
        hasCache &&
        (bookmarks.isStarred(option.id) || defaultItemIds.has(option.id))
      ) {
        return { priority: 1, label: bookmarkGroupLabel };
      }
      if (activeTypes.length > 1 && option.resourceType) {
        const typeIndex = RESOURCE_TYPES_ARRAY.indexOf(option.resourceType);
        return {
          priority: 2 + typeIndex,
          label: RESOURCE_TYPE_PLURAL_LABELS[option.resourceType],
        };
      }
      return { priority: 2, label: allGroupLabel };
    },
    [
      sandboxId,
      sandboxLabel,
      hasCache,
      bookmarks,
      defaultItemIds,
      bookmarkGroupLabel,
      activeTypes,
      allGroupLabel,
    ],
  );

  const options = useMemo((): ResourceItem[] => {
    if (metadataOption) return [metadataOption];
    let result: ResourceItem[];
    // Client-filter mode always shows this "default view" (MUI's own
    // getOptionLabel-based filtering narrows it as the user types), and
    // search mode shows it only while the query is empty. Either way, the
    // sandbox pin + starred/recents dedup logic is the same — the only
    // difference is whether there's a `defaultOptions` full list to layer in.
    if (useClientFilter || showDefaultItems) {
      const built: ResourceItem[] = [];
      const seen = new Set<string>();

      const addOption = (item: ResourceItem) => {
        if (!seen.has(item.id)) {
          built.push({
            ...item,
            resourceType: item.resourceType ?? singleType,
          });
          seen.add(item.id);
        }
      };

      if (sandboxId) {
        const sandboxItem =
          sandboxItemProp ??
          defaultOptions?.find((o) => o.id === sandboxId) ??
          bookmarks.defaultItems.find((o) => o.id === sandboxId);
        if (sandboxItem) addOption(sandboxItem);
      }

      if (hasCache) {
        for (const item of bookmarks.defaultItems) {
          const availableItem = useClientFilter
            ? clientOptionsById.get(item.id)
            : item;
          if (availableItem) addOption(availableItem);
        }
      }

      for (const item of defaultOptions ?? []) {
        addOption(item);
      }

      result = built;
    } else {
      // Backend search results are capped (see MAX_SEARCH_RESULTS) and sorted
      // server-side, so a starred/recent or pinned-sandbox item that matches
      // the query can be pushed past the cutoff and vanish entirely — even
      // though it belongs in the "Suggested"/sandbox group. Locally re-check
      // that small bookmark list against the query and splice any matches
      // back in so they're never silently dropped by truncation.
      const currentIds = new Set(currentItems.map((item) => item.id));
      const extras: ResourceItem[] = [];
      const addExtra = (item: ResourceItem | undefined) => {
        if (
          item &&
          !currentIds.has(item.id) &&
          !extras.some((e) => e.id === item.id) &&
          getOptionLabel(item)
            .toLowerCase()
            .includes(debouncedQuery.toLowerCase())
        ) {
          extras.push(item);
        }
      };

      if (isNameSearch) {
        if (sandboxId) {
          addExtra(
            sandboxItemProp ??
              bookmarks.defaultItems.find((o) => o.id === sandboxId),
          );
        }
        if (hasCache) {
          // Match against the full starred + recents lists, not just the
          // capped `defaultItems` slice used for the empty-query view — the
          // query itself keeps this bounded, so there's no need for the
          // default view's display cap here.
          bookmarks.starredItems.forEach(addExtra);
          bookmarks.recentItems.forEach(addExtra);
        }
      }

      result = extras.length > 0 ? [...extras, ...currentItems] : currentItems;
    }
    // Stable sort (preserves search-relevance order within each group).
    if (shouldGroup) {
      result = result
        .map((item, index) => ({ item, index }))
        .sort((a, b) => {
          const diff =
            resolveGroup(a.item).priority - resolveGroup(b.item).priority;
          return diff !== 0 ? diff : a.index - b.index;
        })
        .map(({ item }) => item);
    }
    return result;
  }, [
    currentItems,
    metadataOption,
    showDefaultItems,
    useClientFilter,
    defaultOptions,
    clientOptionsById,
    bookmarks.defaultItems,
    bookmarks.starredItems,
    bookmarks.recentItems,
    sandboxId,
    sandboxItemProp,
    hasCache,
    singleType,
    shouldGroup,
    resolveGroup,
    isNameSearch,
    debouncedQuery,
    getOptionLabel,
  ]);

  const isOptionEqualToValue = (a: ResourceItem, b: ResourceItem) =>
    a.id === b.id;

  const getOptionGroup = useCallback(
    (option: ResourceItem) => resolveGroup(option).label,
    [resolveGroup],
  );

  // Shared by both the blur handler and the Autocomplete onChange handler
  // below: resolves a free-solo raw string against known options, falling
  // back to `onFreeSoloBlur` (e.g. UserRoleSelector's role-by-name lookup)
  // when nothing matches.
  const resolveFreeSoloText = useCallback(
    (text: string) => {
      const match =
        options.find((o) => getOptionLabel(o) === text || o.id === text) ??
        null;
      if (match) {
        onChange(match as T);
        setInputValue(getOptionLabel(match));
        if (cacheId && (!sandboxId || match.id !== sandboxId)) {
          bookmarks.recordRecent(match);
        }
      } else if (onFreeSoloBlur) {
        onFreeSoloBlur(text);
      }
    },
    [
      options,
      getOptionLabel,
      onChange,
      onFreeSoloBlur,
      cacheId,
      sandboxId,
      bookmarks,
      setInputValue,
    ],
  );

  const handleInputBlur = useCallback(
    (_event: React.FocusEvent<HTMLInputElement>) => {
      if (freeSolo && (!value || clearedByTyping.current) && inputValue) {
        resolveFreeSoloText(inputValue);
      }
    },
    [freeSolo, value, inputValue, resolveFreeSoloText],
  );

  const typeLabels = activeTypes.map((t) => RESOURCE_TYPE_LABELS[t]).join(", ");
  const defaultLabel =
    activeTypes.length === RESOURCE_TYPES_ARRAY.length
      ? "Search resources"
      : activeTypes.length === 1
        ? `Search ${activeTypes[0]}`
        : `Search ${typeLabels}`;
  const defaultPlaceholder =
    activeTypes.length === RESOURCE_TYPES_ARRAY.length
      ? "Search for a resource..."
      : activeTypes.length === 1
        ? `Search for ${activeTypes[0]}...`
        : `Search for ${typeLabels}...`;

  const resolvedLabel = label ?? defaultLabel;
  const resolvedPlaceholder = placeholder ?? defaultPlaceholder;

  return (
    <Autocomplete<ResourceItem>
      sx={sx}
      options={options}
      // Without this, MUI derives each option's React key from its display
      // label, so two distinct resources sharing a label (e.g. a user and a
      // role with the same fullName) collide and render incorrectly.
      getOptionKey={(option) => option.id}
      value={value}
      autoHighlight
      selectOnFocus={!freeSolo}
      onChange={(
        _event: React.SyntheticEvent,
        newValue: ResourceItem | string | null,
      ) => {
        // MUI's own Enter-key handling for freeSolo inputs commits the raw
        // typed text directly here (reason "createOption"), bypassing our
        // blur-time resolution entirely — without this, it would fall
        // through to the cast below and corrupt the value (e.g. typing an
        // unrecognized role name/id and pressing Enter would silently wipe
        // the selection instead of resolving or passing the text through).
        if (typeof newValue === "string") {
          if (freeSolo) {
            resolveFreeSoloText(newValue);
          }
          return;
        }
        // ResourceItem is a lens over the full API type; the object keeps all
        // its fields. Casting to T here lets callers get a typed result.
        onChange(newValue as T | null);
        clearPending.current = newValue === null;
        if (newValue && cacheId && (!sandboxId || newValue.id !== sandboxId)) {
          bookmarks.recordRecent(newValue);
        }
      }}
      inputValue={inputValue}
      onInputChange={(
        _event: React.SyntheticEvent,
        newInputValue: string,
        reason: string,
      ) => {
        if (
          reason === "reset" &&
          (!newInputValue || clearPending.current || clearedByTyping.current)
        ) {
          return;
        }
        setInputValue(newInputValue);
        if (reason === "input" && value) {
          // We're keeping the typed text; tell the value→null effect not to
          // wipe it when this clears the selection.
          clearedByTyping.current = true;
          onChange(null);
        }
      }}
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={isOptionEqualToValue}
      renderOption={(props, option) => {
        const { key, ...rest } = props as typeof props & { key: string };
        // Show the type icon when searching multiple types; in single-type mode the
        // type is already implied, so the icon would be noise.
        const Icon =
          activeTypes.length !== 1 && option.resourceType
            ? RESOURCE_TYPE_ICONS[option.resourceType]
            : null;
        const isSandbox = sandboxId && option.id === sandboxId;
        const starred = hasCache && bookmarks.isStarred(option.id);
        const recent = hasCache && !starred && defaultItemIds.has(option.id);
        return (
          <li key={key} {...rest}>
            <OptionContainer>
              {Icon && option.resourceType && (
                <DescriptiveTooltip
                  title={RESOURCE_TYPE_LABELS[option.resourceType]}
                >
                  <Box
                    component="span"
                    sx={{ display: "inline-flex", flexShrink: 0 }}
                  >
                    <Icon fontSize="small" color="action" />
                  </Box>
                </DescriptiveTooltip>
              )}
              {renderOptionIcon?.(option as T)}
              <OptionDetails>
                <Typography variant="body2" noWrap>
                  {getOptionLabel(option)}
                </Typography>
              </OptionDetails>
              {hasCache && !isSandbox && (
                <ResourceStarButton
                  starred={starred}
                  recent={recent}
                  onToggle={() => bookmarks.toggleStar(option)}
                />
              )}
            </OptionContainer>
          </li>
        );
      }}
      loading={isLoading}
      disabled={disabled}
      filterOptions={
        useClientFilter ? filterOptionsForced : (x: ResourceItem[]) => x
      }
      groupBy={shouldGroup ? getOptionGroup : undefined}
      noOptionsText={
        noOptionsTextOverride ??
        (useClientFilter
          ? activeTypes.length === RESOURCE_TYPES_ARRAY.length
            ? "No resources found"
            : `No ${typeLabels} found`
          : inputValue.length < MIN_SEARCH_LENGTH
            ? `Type at least ${MIN_SEARCH_LENGTH} characters to search`
            : activeTypes.length === RESOURCE_TYPES_ARRAY.length
              ? "No resources found"
              : `No ${typeLabels} found`)
      }
      renderInput={(params) => {
        const Icon =
          value && activeTypes.length !== 1 && value.resourceType
            ? RESOURCE_TYPE_ICONS[value.resourceType]
            : null;
        const { inputProps, ...textFieldParams } = params;
        return (
          <TextField
            {...textFieldParams}
            label={resolvedLabel}
            placeholder={resolvedPlaceholder}
            variant="outlined"
            required={required}
            autoFocus={autoFocus}
            error={Boolean(error)}
            helperText={error || undefined}
            InputLabelProps={inputLabelProps}
            inputProps={{
              ...inputProps,
              // Only overrides the accessible name when the caller supplies one
              // (visible-label instances leave this undefined, so React omits it).
              "aria-label": ariaLabel,
              onBlur: (e) => {
                const blurEvent = e as React.FocusEvent<HTMLInputElement>;
                inputProps.onBlur?.(blurEvent);
                handleInputBlur(blurEvent);
              },
            }}
            InputProps={{
              ...textFieldParams.InputProps,
              startAdornment:
                startIcon || Icon ? (
                  <>
                    {startIcon}
                    {Icon ? (
                      <Box
                        component="span"
                        sx={{
                          display: "inline-flex",
                          flexShrink: 0,
                          ml: 1,
                          mr: 1,
                        }}
                      >
                        <Icon fontSize="small" color="action" />
                      </Box>
                    ) : null}
                  </>
                ) : null,
              endAdornment: (
                <>
                  {isLoading ? (
                    <CircularProgress color="inherit" size={20} />
                  ) : null}
                  {textFieldParams.InputProps.endAdornment}
                </>
              ),
            }}
          />
        );
      }}
    />
  );
}

export default ResourceSelector;
